import React, { useState } from 'react';
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Editor, { useMonaco } from '@monaco-editor/react';
import { useMutation, useQuery } from 'react-fetching-library';
import { Modal, Card, CardContent, Stack, Button, Portal, Typography, Box, CardHeader, TextField, Select, MenuItem } from '@mui/material';
import parse from 'html-react-parser';
import { createDynamicSection, getAgreementDynamicSections } from '../../../services/request';
import htmlhint from 'htmlhint';
import { SmallFormTextField } from '../../../components/small-fields/SmallFormTextField';

const validationSchema = Yup.object({
  agreement_template: Yup.string().required('To pole nie może być puste'),
  agreement_section_type: Yup.number().required('To pole nie może być puste'),
  agreement_type: Yup.string().required('To pole nie może być puste')
});

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const htmlSchemaDefault = '';

export default function DynamicSectionsUpdateCreate ({ open, setOpen, setDynamicSections, form = null }) {
  const monaco = useMonaco();
  const editorRef = React.useRef(null);
  const { mutate, error, reset } = useMutation(createDynamicSection);
  const [htmlSchema, setHtmlSchema] = useState(_.isNil(form) ? htmlSchemaDefault : form.content);
  const [htmlErrors, setHtmlErrors] = useState([]);
  const [choices, setChoices] = useState([]);
  const optionsRequest = useQuery(getAgreementDynamicSections('OPTIONS'));

  React.useEffect(() => {
    if (!optionsRequest.loading && !optionsRequest.error) {
      setChoices(optionsRequest.payload.actions.GET.choices);
    }
  }, [optionsRequest.loading]);


  const handleHtmlSchemaChange = value => {
    setHtmlSchema(value);
  };

  const handleValidate = () => {
    if (monaco && editorRef.current) { // Sprawdzenie czy monaco jest dostępny
      const errors = htmlhint.HTMLHint.verify(htmlSchema, {
        'tagname-lowercase': true,
        'attr-lowercase': true,
        'attr-value-double-quotes': true,
        'doctype-first': true,
        'id-unique': true,
        'src-not-empty': true,
        'img-alt-require': true
      });

      const monacoErrors = errors.map(error => ({
        startLineNumber: error.line,
        startColumn: error.col,
        endLineNumber: error.line,
        endColumn: error.col + 1,
        message: error.message,
        severity: monaco.MarkerSeverity.Error // Użycie MarkerSeverity z monaco
      }));

      setHtmlErrors(errors);

      const model = editorRef.current.getModel();
      monaco.editor.setModelMarkers(model, 'html', monacoErrors);
    }
  };

  const handleOnMount = editor => {
    editorRef.current = editor;
    setTimeout(() => editor.getAction('editor.action.formatDocument').run(), 300);
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, _.isNil(form) ? 'POST' : 'PATCH', _.isNil(form) ? null : form.id);

    if (!error) {
      if (_.isNil(form)) {
        setDynamicSections(prev => [payload, ...prev]);
      } else {
        setDynamicSections(prev => prev.map(item => item.id === payload.id ? payload : item));
      }
      reset();
      formik.resetForm();
      setHtmlSchema(htmlSchemaDefault);
      setOpen(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      agreement_template: _.isNil(form) ? '' : form.agreement_template,
      agreement_section_type: _.isNil(form) ? '' : form.agreement_section_type,
      agreement_type: _.isNil(form) ? '' : form.agreement_type
    },
    validateOnChange: false,
    validationSchema,
    onSubmit: values => {
      values.content = htmlSchema;
      handleSubmit(values);
    }
  });

  return (
    <Portal>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Card sx={{ ...style, width: '85vw' }}>
          <CardContent>
            <form onSubmit={formik.handleSubmit}>
              <Stack direction="row" sx={{ mb: 2 }} spacing={5}>
                <SmallFormTextField
                  select
                  name="type"
                  placeholder="Szablon"
                  label="Szablon"
                  onChange={e => formik.setFieldValue('agreement_template', e.target.value)}
                  value={formik.values.agreement_template}
                  sx={{ width: '100%' }}
                  options={choices?.agreement_template || []}
                  error={formik.errors.agreement_template && formik.touched.agreement_template}
                  helperText={formik.errors.agreement_template}
                />
                <TextField
                  id="agreement_section_type"
                  size="small"
                  label="Rodzaj sekcji"
                  type="number"
                  fullWidth
                  value={formik.values.agreement_section_type}
                  onChange={formik.handleChange}
                  error={formik.errors.agreement_section_type && formik.touched.agreement_section_type}
                  helperText={formik.errors.agreement_section_type}
                />
              </Stack>
              <Stack>
                <TextField
                  id="Rodzaj umowy"
                  size="small"
                  label="rodzaj umowy"
                  sx={{ mb: 2 }}
                  value={formik.values.agreement_type}
                  onChange={formik.handleChange}
                  error={formik.errors.agreement_type && formik.touched.agreement_type}
                  helperText={formik.errors.agreement_type}
                />
              </Stack>
              <Stack direction="row" spacing={5}>
                <Stack sx={{ flex: '1 1 50%' }}>
                  <CardHeader title="HTML"/>
                  <Editor
                    height="500px"
                    language="html"
                    theme="vs-dark"
                    value={htmlSchema}
                    onMount={handleOnMount}
                    onChange={handleHtmlSchemaChange}
                    onValidate={handleValidate}
                  />
                </Stack>
                <Stack sx={{ flex: '1 1 50%' }}>
                  <CardHeader title="Podgląd HTML"/>
                  <Box
                    sx={{
                      px: 4,
                      py: 1,
                      height: '500px',
                      overflow: 'auto',
                      borderRadius: 2,
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.19)', // głębia cienia
                      border: '1px solid rgba(0, 0, 0, 0.1)' // subtelna granica, dodająca wymiaru
                    }}
                  >
                    {parse(htmlSchema)}
                  </Box>
                </Stack>
              </Stack>
              <Stack direction="row" sx={{ mt: 4 }} spacing={5}>
                <Button variant="outlined" sx={{ width: '100%' }} onClick={() => setOpen(false)}>Anuluj</Button>
                <Button
                  disabled={htmlSchema.trim() === ''}
                  variant="contained"
                  sx={{ width: '100%' }}
                  type="submit">
                  {_.isNil(form) ? 'Dodaj' : 'Edytuj'}
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Modal>
    </Portal>
  );
}
